import {
	lazy,
	Suspense,
	useContext,
	useEffect,
	useState,
	useCallback,
	useRef,
} from "react";
import { Context } from "Context";
import useFetch from "hooks/useFetch";
import appConfig from "config.json";
import { Loading } from "@wdpr/groot-components";
import { mapString, isTravelAgent, toggleBodyClass } from "utils";
import { getCurrentDateInSelectedLocale } from "utils/date";
import { filterContentByUserType } from "utils/filterContent";
import NavigationTxp from "./navigation/navigation-txp/NavigationTxp";

// lazy loaded components
const Banner = lazy(() => import("./banner/Banner"));
const MessageBanner = lazy(() => import("./MessageBanner"));
const SmartBanner = lazy(() => import("./SmartBanner"));
const TravelAgentHeader = lazy(() =>
	import("../travel-agent-header/TravelAgentHeader")
);
const MessageCenter = lazy(() =>
	import("../message-center/MessageCenterWrapper")
);

const { brands, defaultParkHoursTimeZone, content } = appConfig;
const cacheMinutes = 5;
const mainClass = "syndicated-header";
const txpHoverClass = "navigation-nav__txp--hover";

const HeaderTxp = () => {
	const {
		config,
		headerData,
		setHeaderData,
		setIsHeaderLoading,
		setConfig,
		profileData: { userType, user },
		isMobileView,
		cdnUrl,
		isSticky,
	} = useContext(Context);

	const headerRef = useRef(null);
	const [headerUrl, setHeaderUrl] = useState("");
	const [isHeaderHovered, setIsHeaderHovered] = useState(false);
	const [navHover, setNavHover] = useState(false);
	const [navFocus, setNavFocus] = useState(false);
	const [searchBarToggle, setSearchBarToggle] = useState(false);
	const brandConfig = brands?.[config.brand];

	const parkHoursTimeZone =
		brandConfig?.defaultParkHoursTimeZone || defaultParkHoursTimeZone;
	const isUserTypeRequired = brandConfig?.isUserTypeRequiredForContentRequest;
	const { response, error } = useFetch(headerUrl, {}, cacheMinutes, headerUrl);

	let classes = `${mainClass} ${mainClass}--${config.brand} ${mainClass}--${config.locale}`;
	if (config.position) {
		classes += ` ${mainClass}--${config.position}`;
		if (isSticky) {
			const parentDiv = document?.querySelector(".syndicated-header-parent");
			parentDiv.classList.add("syndicated-header-parent--sticky");
		}
	}

	if (config.store) {
		classes += ` ${mainClass}__store ${mainClass}__store--${config.store}`;
	}

	classes += ` ${mainClass}--txp`;

	if (config.theme) {
		classes += ` syndicated-theme--${config.theme}`;
	}

	if (config.hideFullNavigation) {
		classes += ` ${mainClass}--hidden`;
	}

	useEffect(() => {
		if (!isUserTypeRequired || (isUserTypeRequired && userType)) {
			const headerBaseUrl = mapString(
				content.apiUrl.header,
				getCurrentDateInSelectedLocale(parkHoursTimeZone, "ISO", "en-us"),
				true
			);
			setHeaderUrl(
				isUserTypeRequired && userType
					? `${headerBaseUrl}&userType=${userType}&isMobileView=${isMobileView}`
					: `${headerBaseUrl}&isMobileView=${isMobileView}`
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userType, config.brand]);

	useEffect(() => {
		if (response) {
			/*
			 *if response is received, set loading to false no matther what *response is comming
			 */
			setIsHeaderLoading(false);
			if (brandConfig.filterContentKeys) {
				setHeaderData({
					...filterContentByUserType(
						response,
						brandConfig.filterContentKeys,
						user
					),
					loading: false,
				});
			} else {
				setHeaderData({ ...response, loading: false });
			}
			setConfig((config) => ({
				...config,
				...(response.context || {}),
			}));
		} else if (error) {
			setHeaderData({ error: true, loading: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, response]);

	useEffect(() => {
		if (brandConfig?.filterContentKeys && response) {
			setHeaderData(
				filterContentByUserType(response, brandConfig.filterContentKeys, user)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.swid, user?.membership]);

	const toggleHoverClass = useCallback(
		(toggle) => {
			if (!isMobileView) {
				toggleBodyClass(toggle, txpHoverClass);
			}
			setIsHeaderHovered(toggle);
		},
		[isMobileView]
	);

	useEffect(() => {
		if (!searchBarToggle) {
			setNavFocus(false);
		}
	}, [searchBarToggle]);

	useEffect(() => {
		if (!navFocus && !navHover) {
			if (!searchBarToggle) {
				toggleHoverClass(false);
			}
		}
		if (navFocus || navHover) {
			toggleHoverClass(true);
		}
	}, [navHover, navFocus, searchBarToggle, toggleHoverClass]);

	useEffect(() => {
		if (!searchBarToggle) return;
		const handleClickOutside = (e) => {
			if (headerRef.current && !headerRef.current.contains(e.target)) {
				setSearchBarToggle(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [searchBarToggle]);

	useEffect(() => {
		toggleBodyClass(false, txpHoverClass);
		setIsHeaderHovered(false);
		setNavHover(false);
		setNavFocus(false);
		setSearchBarToggle(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobileView]);

	return (
		<>
			{config.messageCenter && !document?.getElementById("messageCenter") && (
				<Suspense fallback={null}>
					<MessageCenter />
				</Suspense>
			)}
			{config.travelAgentHeader &&
				(config.store || (isUserTypeRequired && isTravelAgent(userType))) && (
					<Suspense fallback={null}>
						<TravelAgentHeader />
					</Suspense>
				)}
			<div
				ref={headerRef}
				className={classes}
				onMouseEnter={() => setNavHover(true)}
				onMouseLeave={() => {
					setNavHover(false);
					setNavFocus(false);
				}}
				onFocus={() => setNavFocus(true)}
				onBlur={() => setNavFocus(false)}
				style={{ visibility: "hidden" }}
			>
				{config.smartBanner && (
					<Suspense fallback={null}>
						<SmartBanner />
					</Suspense>
				)}
				{config.banner && (
					<Suspense fallback={null}>
						<Banner />
					</Suspense>
				)}

				{headerData.loading && <Loading brand={config.brand} cdnUrl={cdnUrl} />}
				{!config.hideFullNavigation &&
					!headerData.loading &&
					!headerData.error && (
						<NavigationTxp
							showLogo={!config.navigation && !config.chrome}
							headerRef={headerRef}
							isHeaderHovered={isHeaderHovered}
							searchBarToggle={searchBarToggle}
							setSearchBarToggle={setSearchBarToggle}
						/>
					)}
				{config.messageBanner && (
					<Suspense fallback={null}>
						<MessageBanner />
					</Suspense>
				)}
				<div id="pageContentStart" tabIndex={-1} />
			</div>
		</>
	);
};

export default HeaderTxp;
